<template>
    <UIDialog :open="open" @update:open="$emit('close')">
        <DialogScrollContent v-if="conversation" class="flex max-h-[90vh] max-w-4xl flex-col border-none p-0">
            <DialogHeader class="flex-shrink-0 border-b border-slate-100">
                <DialogTitle class="px-6 py-4">
                    <div class="flex items-center justify-between">
                        <div class="mr-4 flex-1">
                            <h3 class="text-lg font-semibold text-slate-900">{{ conversation.subject }}</h3>
                            <div class="mt-1 flex items-center space-x-4 text-sm text-slate-500">
                                <span>{{ conversation.message_count }} messages</span>
                                <span>•</span>
                                <span>{{ conversation.creator_email }}</span>
                                <span v-if="conversation.campaign" class="text-indigo-600">• Campaign linked</span>
                            </div>
                        </div>
                        <div class="mr-8 flex-shrink-0">
                            <button class="btn-sm bg-indigo-500 text-white hover:bg-indigo-600" @click="openReplyModal">
                                <AppIcon name="reply" class="mr-1 h-3 w-3" />
                                Reply
                            </button>
                        </div>
                    </div>
                </DialogTitle>
            </DialogHeader>

            <div class="flex-shrink-0 border-b border-slate-100 bg-slate-50 px-6 py-3">
                <div class="flex items-center justify-between text-sm">
                    <div class="flex items-center space-x-6">
                        <div>
                            <span class="font-medium text-slate-700">Initiated by:</span>
                            <span class="ml-1">
                                {{ conversation.email_account?.operator?.first_name }}
                                {{ conversation.email_account?.operator?.last_name }}
                            </span>
                        </div>
                        <div>
                            <span class="font-medium text-slate-700">From:</span>
                            <span class="ml-1">{{ conversation.email_account?.email_address }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-slate-700">Status:</span>
                            <Badge
                                class="ml-1"
                                :variant="
                                    {
                                        active: 'success',
                                        archived: 'secondary',
                                        snoozed: 'outline',
                                    }[conversation.status]
                                "
                            >
                                {{ conversation.status }}
                            </Badge>
                        </div>
                    </div>
                </div>
            </div>

            <div ref="scrollArea" class="min-h-0 flex-1 overflow-y-auto">
                <div class="space-y-6 px-4 py-6">
                    <div v-if="state.loadingMessages" class="flex justify-center py-8">
                        <AppIcon
                            name="spinner-third"
                            class="h-6 w-6 animate-spin text-slate-400"
                        />
                    </div>

                    <div v-else-if="messages.length === 0" class="py-8 text-center text-slate-500">
                        <AppIcon name="envelope" class="mb-2 h-8 w-8" />
                        <p>No messages found.</p>
                    </div>

                    <div v-else class="space-y-4">
                        <div
                            v-for="message in messages"
                            :key="message.id"
                            class="flex"
                            :class="{
                                'justify-start': message.direction === 'inbound',
                                'justify-end': message.direction === 'outbound',
                            }"
                        >
                            <div
                                class="max-w-[70%] rounded-2xl shadow-sm"
                                :class="{
                                    'border border-slate-200 bg-white': message.direction === 'inbound',
                                    'bg-indigo-500 text-white': message.direction === 'outbound',
                                }"
                            >
                                <div
                                    class="border-b px-4 py-2"
                                    :class="{
                                        'border-slate-100': message.direction === 'inbound',
                                        'border-indigo-400': message.direction === 'outbound',
                                    }"
                                >
                                    <div class="flex items-start justify-between">
                                        <!-- Sender + Date -->
                                        <div class="flex items-start space-x-2">
                                            <div
                                                class="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium"
                                                :class="{
                                                    'bg-slate-100 text-slate-600': message.direction === 'inbound',
                                                    'bg-indigo-400 text-white': message.direction === 'outbound',
                                                }"
                                            >
                                                {{ getInitials(message.from_name || message.from_email) }}
                                            </div>
                                            <div>
                                                <div
                                                    class="text-sm font-medium"
                                                    :class="{
                                                        'text-slate-900': message.direction === 'inbound',
                                                        'text-white': message.direction === 'outbound',
                                                    }"
                                                >
                                                    {{ message.from_name || message.from_email }}
                                                </div>
                                                <div
                                                    class="mt-0.5 text-xs"
                                                    :class="{
                                                        'text-slate-500': message.direction === 'inbound',
                                                        'text-indigo-100': message.direction === 'outbound',
                                                    }"
                                                >
                                                    {{ $filters.formatDateAndTime(message.sent_at) }}
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Reply + Unread -->
                                        <div class="ml-4 mt-0.5 flex flex-col items-end space-y-1">
                                            <span
                                                v-if="message.direction === 'inbound' && !message.is_read"
                                                class="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                                            >
                                                Unread
                                            </span>
                                            <button
                                                class="mt-0.5 text-xs underline"
                                                :class="{
                                                    'text-slate-400 hover:text-slate-600':
                                                        message.direction === 'inbound',
                                                    'text-indigo-100 hover:text-white':
                                                        message.direction === 'outbound',
                                                }"
                                                @click="replyToMessage(message)"
                                            >
                                                Reply
                                            </button>
                                        </div>
                                    </div>

                                    <!-- To / CC section -->
                                    <div
                                        v-if="message.to_emails?.length || message.cc_emails?.length"
                                        class="mt-2 text-xs"
                                        :class="{
                                            'text-slate-600': message.direction === 'inbound',
                                            'text-indigo-100': message.direction === 'outbound',
                                        }"
                                    >
                                        <div v-if="message.to_emails?.length">
                                            <span class="opacity-75">To:</span> {{ message.to_emails.join(', ') }}
                                        </div>
                                        <div v-if="message.cc_emails?.length" class="mt-1">
                                            <span class="opacity-75">CC:</span> {{ message.cc_emails.join(', ') }}
                                        </div>
                                    </div>
                                </div>

                                <div class="px-4 py-3">
                                    <div
                                        class="email-message-body max-w-full overflow-hidden text-sm leading-relaxed"
                                        :class="{
                                            'email-message-body--inbound text-slate-900':
                                                message.direction === 'inbound',
                                            'email-message-body--outbound text-white': message.direction === 'outbound',
                                        }"
                                        style="word-wrap: break-word; overflow-wrap: break-word"
                                        v-html="messageBodyHtmlById[message.id] || ''"
                                    ></div>

                                    <div
                                        v-if="getNonImageAttachments(message.attachments).length"
                                        class="mt-3 border-t pt-3"
                                        :class="{
                                            'border-slate-100': message.direction === 'inbound',
                                            'border-indigo-400': message.direction === 'outbound',
                                        }"
                                    >
                                        <div
                                            class="mb-2 text-xs font-medium"
                                            :class="{
                                                'text-slate-600': message.direction === 'inbound',
                                                'text-indigo-100': message.direction === 'outbound',
                                            }"
                                        >
                                            Attachments:
                                        </div>

                                        <div class="space-y-1">
                                            <div
                                                v-for="attachment in getNonImageAttachments(message.attachments)"
                                                :key="attachment.id"
                                                class="flex cursor-pointer items-center text-sm hover:underline"
                                                :class="{
                                                    'text-slate-600': message.direction === 'inbound',
                                                    'text-indigo-100': message.direction === 'outbound',
                                                }"
                                                @click="downloadAttachment(attachment, message)"
                                            >
                                                <AppIcon
                                                    :name="getFileIcon(attachment)"
                                                    class="mr-2 h-3 w-3"
                                                />
                                                {{ attachment.filename }}
                                                <span v-if="attachment.size" class="ml-2 text-xs opacity-75">
                                                    ({{ formatFileSize(attachment.size) }})
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Quick reply box -->
            <div class="flex-shrink-0 border-t border-slate-200 bg-slate-50 p-3">
                <div class="flex flex-col gap-2">
                    <!-- Attachments (right-aligned above textarea) -->
                    <div class="flex justify-end">
                        <FileAttachments simple :attachments="attachments" @update:attachments="attachments = $event" />
                    </div>

                    <!-- Editor -->
                    <div class="min-w-0">
                        <div class="min-w-0 flex-1 overflow-hidden rounded border border-slate-300 bg-white">
                            <div class="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 p-1">
                                <button
                                    v-for="item in quickReplyFormattingButtons"
                                    :key="item.label"
                                    type="button"
                                    class="quick-reply-toolbar-button"
                                    :class="{ 'is-active': isQuickReplyFormattingActive(item.active) }"
                                    :title="item.title"
                                    @click="runQuickReplyFormattingCommand(item.command)"
                                >
                                    <AppIcon
                                        v-if="item.icon"
                                        :name="item.icon"
                                        class="h-3.5 w-3.5"
                                    />
                                    <span v-else :class="item.class">{{ item.label }}</span>
                                </button>
                            </div>
                            <EditorContent :editor="quickReplyEditor" class="quick-reply-editor" />
                        </div>
                    </div>

                    <div class="flex justify-end">
                        <Button
                            type="button"
                            class="min-w-[5.75rem]"
                            :disabled="!canSendQuickReply || isSendingQuickReply"
                            @click="sendQuickReply"
                        >
                            <AppIcon
                                :name="isSendingQuickReply ? 'spinner-third' : 'paper-plane'"
                                :class="{ 'animate-spin': isSendingQuickReply }"
                                class="mr-1.5 h-3.5 w-3.5"
                                stroke-width="2.5"
                            />
                            Send
                        </Button>
                    </div>
                </div>
            </div>
        </DialogScrollContent>
    </UIDialog>
</template>

<script>
import { Dialog, DialogHeader, DialogTitle, DialogScrollContent } from '@/components/ui/dialog'
import emailAPI from '@/utils/api/api.email'
import { useEmailBodyProcessor } from '@/composables/email/useEmailBodyProcessor'
import { useInlineImageLoader } from '@/composables/email/useInlineImageLoader'
import FileAttachments from './FileAttachments.vue'
import { Badge } from '@/components/ui/badge'
import { useFileAttachments } from '@/composables/email/useFileAttachments'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { Button } from '@/components/ui/button'

export default {
    name: 'ConversationViewModal',

    components: {
        UIDialog: Dialog,
        DialogHeader,
        DialogTitle,
        DialogScrollContent,
        Badge,
        FileAttachments,
        EditorContent,
        Button,
    },

    props: {
        open: { type: Boolean, default: false },
        conversation: { type: Object, default: null },
    },

    emits: ['close', 'reload', 'reply'],

    data() {
        const attachments = []
        const { getFileIcon, formatFileSize } = useFileAttachments({ value: attachments })

        return {
            messages: [],
            messageBodyHtmlById: {},
            messageBodyImagePlaceholdersById: {},
            state: {
                loadingMessages: false,
            },
            bodyProcessor: useEmailBodyProcessor(),
            imageLoader: useInlineImageLoader(async (attachmentId, messageId) => {
                const result = await emailAPI.downloadAttachment(this.$axios, attachmentId, messageId)
                if (result.success) {
                    return result.value
                }
                this.$errorHandler('Download failed')
            }),
            attachments,
            getFileIcon,
            formatFileSize,
            quickReplyEditor: null,
            quickReplyText: '',
            quickReplySelectionVersion: 0,
            isSendingQuickReply: false,
        }
    },

    computed: {
        quickReplyFormattingButtons() {
            return [
                { label: 'B', title: 'Bold', active: 'bold', command: 'bold', class: 'font-bold' },
                { label: 'I', title: 'Italic', active: 'italic', command: 'italic', class: 'italic' },
                { label: 'U', title: 'Underline', active: 'underline', command: 'underline', class: 'underline' },
                {
                    label: 'Quote',
                    title: 'Block quote',
                    active: 'blockquote',
                    command: 'blockquote',
                    icon: 'quote-left',
                },
                {
                    label: 'Bullet',
                    title: 'Bullet list',
                    active: 'bulletList',
                    command: 'bulletList',
                    icon: 'list-ul',
                },
                {
                    label: 'Numbered',
                    title: 'Numbered list',
                    active: 'orderedList',
                    command: 'orderedList',
                    icon: 'list-ol',
                },
                {
                    label: 'Clear',
                    title: 'Clear formatting',
                    active: null,
                    command: 'clear',
                    icon: 'eraser',
                },
            ]
        },

        canSendQuickReply() {
            return !!this.quickReplyText.trim()
        },
    },

    watch: {
        open(newVal, oldVal) {
            if (newVal && !oldVal && this.conversation?.id) {
                this.loadMessages()
                this.$nextTick(() => this.scrollToBottom())
            }
        },
        conversation: {
            deep: true,
            handler(newVal) {
                if (this.open && newVal?.id) {
                    this.loadMessages()
                }
            },
        },
    },

    mounted() {
        this.quickReplyEditor = new Editor({
            extensions: [
                StarterKit.configure({
                    bulletList: true,
                    listItem: true,
                    orderedList: true,
                    blockquote: true,
                }),
                Underline,
            ],
            content: '',
            onUpdate: ({ editor }) => {
                this.quickReplyText = editor.getText()
                this.quickReplySelectionVersion += 1
            },
            onSelectionUpdate: () => {
                this.quickReplySelectionVersion += 1
            },
        })

        if (this.open && this.conversation) {
            this.loadMessages()
            this.$nextTick(() => this.scrollToBottom())
        }
    },

    beforeUnmount() {
        if (this.quickReplyEditor) {
            this.quickReplyEditor.destroy()
        }
    },

    methods: {
        async loadMessages() {
            if (!this.conversation?.id) return
            this.state.loadingMessages = true
            try {
                const result = await emailAPI.getMessagesForConversation(this.$axios, this.conversation.id, {
                    perPage: 50,
                    sortby: 'sent_at',
                    sortdir: 'asc',
                })
                if (result.success) {
                    const messages = result.value.records || result.value.messages || result.value || []
                    this.prepareMessageBodies(messages)
                    this.messages = messages
                    this.state.loadingMessages = false
                    await this.$nextTick()
                    this.loadMessageBodyImages()
                    this.scrollToBottom()
                } else {
                    this.$errorHandler('Failed to load messages')
                    this.messages = []
                    this.messageBodyHtmlById = {}
                    this.messageBodyImagePlaceholdersById = {}
                }
            } catch {
                this.$errorHandler('Failed to load messages')
                this.messages = []
                this.messageBodyHtmlById = {}
                this.messageBodyImagePlaceholdersById = {}
            } finally {
                this.state.loadingMessages = false
            }
        },

        scrollToBottom() {
            const el = this.$refs.scrollArea
            if (!el) return
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight
            })
        },

        getInitials(name) {
            if (!name) {
                return '?'
            }

            return name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2)
        },

        prepareMessageBodies(messages = []) {
            const htmlById = {}
            const imagePlaceholdersById = {}

            messages.forEach((message) => {
                const { html, cidPlaceholders, inlinePlaceholders } = this.bodyProcessor.cleanBody(message)
                htmlById[message.id] = html
                imagePlaceholdersById[message.id] = {
                    cidPlaceholders,
                    inlinePlaceholders,
                }
            })

            this.messageBodyHtmlById = htmlById
            this.messageBodyImagePlaceholdersById = imagePlaceholdersById
        },

        loadMessageBodyImages() {
            this.messages.forEach((message) => {
                const placeholders = this.messageBodyImagePlaceholdersById[message.id]

                if (!placeholders) {
                    return
                }

                this.imageLoader.loadCidImages(placeholders.cidPlaceholders, message.id)
                this.imageLoader.loadInlineImages(placeholders.inlinePlaceholders, message.id)
            })
        },

        getNonImageAttachments(attachments) {
            return this.bodyProcessor.getNonImageAttachments(attachments || [])
        },

        async downloadAttachment(attachment, message) {
            try {
                const result = await emailAPI.downloadAttachment(this.$axios, attachment.id, message.id)
                if (!result.success) {
                    this.$errorHandler('Failed to download attachment')
                    return
                }

                const blob = new Blob([result.value])
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = attachment.filename || `attachment_${attachment.id}`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch {
                this.$errorHandler('Failed to download attachment')
            }
        },

        openReplyModal() {
            this.$emit('reply', { type: 'conversation', conversation: this.conversation })
        },

        replyToMessage(message) {
            this.$emit('reply', { type: 'message', message, conversation: this.conversation })
        },

        isQuickReplyFormattingActive(markOrNode) {
            if (!markOrNode || !this.quickReplyEditor) {
                return false
            }

            return this.quickReplyEditor.isActive(markOrNode)
        },

        runQuickReplyFormattingCommand(command) {
            if (!this.quickReplyEditor) {
                return
            }

            const chain = this.quickReplyEditor.chain().focus()
            const commands = {
                bold: () => chain.toggleBold().run(),
                italic: () => chain.toggleItalic().run(),
                underline: () => chain.toggleUnderline().run(),
                blockquote: () => chain.toggleBlockquote().run(),
                bulletList: () => chain.toggleBulletList().run(),
                orderedList: () => chain.toggleOrderedList().run(),
                clear: () => chain.unsetAllMarks().clearNodes().run(),
            }

            commands[command]?.()
        },

        async sendQuickReply() {
            if (!this.canSendQuickReply) return
            this.isSendingQuickReply = true

            try {
                if (!this.messages?.length) {
                    this.$errorHandler('No messages found')
                    this.isSendingQuickReply = false
                    return
                }

                const latestMessage = this.messages[this.messages.length - 1]
                const body = this.quickReplyEditor.getHTML()
                const replyData = {
                    body,
                    content_type: 'text/html',
                    attachments: this.attachments,
                }

                const response = await emailAPI.replyToMessage(this.$axios, latestMessage.id, replyData)

                if (response.success) {
                    this.quickReplyEditor.commands.setContent('')
                    this.quickReplyText = ''
                    this.attachments = []
                    this.$successHandler('Reply sent')
                    await this.loadMessages()
                } else {
                    this.$errorHandler('Failed to send reply')
                }
            } catch (err) {
                this.$errorHandler('Error sending reply')
            } finally {
                this.isSendingQuickReply = false
            }
        },
    },
}
</script>

<style scoped>
.quick-reply-toolbar-button {
    display: inline-flex;
    min-height: 1.75rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: rgb(51 65 85);
}

.quick-reply-toolbar-button:hover,
.quick-reply-toolbar-button.is-active {
    background-color: rgb(226 232 240);
    color: rgb(15 23 42);
}

.quick-reply-editor :deep(.ProseMirror) {
    max-height: 9rem;
    min-height: 4.5rem;
    overflow-y: auto;
    padding: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
}

.quick-reply-editor :deep(.ProseMirror:focus),
.quick-reply-editor :deep(.ProseMirror:focus-visible) {
    outline: none;
}

.quick-reply-editor :deep(.ProseMirror blockquote) {
    margin: 0.5rem 0;
    border-left: 3px solid rgb(203 213 225);
    padding-left: 0.75rem;
    color: rgb(71 85 105);
}

.quick-reply-editor :deep(.ProseMirror ul),
.quick-reply-editor :deep(.ProseMirror ol) {
    margin: 0.5rem 0;
    padding-left: 1.25rem;
}

.quick-reply-editor :deep(.ProseMirror ul) {
    list-style-type: disc;
}

.quick-reply-editor :deep(.ProseMirror ol) {
    list-style-type: decimal;
}

.email-message-body :deep(p) {
    margin: 0.5rem 0;
}

.email-message-body :deep(p:first-child) {
    margin-top: 0;
}

.email-message-body :deep(p:last-child) {
    margin-bottom: 0;
}

.email-message-body :deep(a) {
    color: rgb(79 70 229);
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.email-message-body--outbound :deep(a) {
    color: #fff;
}

.email-message-body :deep(ul),
.email-message-body :deep(ol) {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
}

.email-message-body :deep(ul) {
    list-style-type: disc;
}

.email-message-body :deep(ol) {
    list-style-type: decimal;
}

.email-message-body :deep(li) {
    margin: 0.25rem 0;
    padding-left: 0.125rem;
}

.email-message-body :deep(blockquote) {
    margin: 0.75rem 0;
    border-left: 3px solid rgb(203 213 225);
    padding: 0.25rem 0 0.25rem 0.75rem;
    color: rgb(71 85 105);
}

.email-message-body--outbound :deep(blockquote) {
    border-left-color: rgb(199 210 254);
    color: rgb(238 242 255);
}
</style>
